import { useStyles } from './SLPInput.styles';
import { SLPInputProps } from './SLPInput.props';
import { getInnerHTMLLengthExcludingTokens } from './SLPInput.utils';
import { debounce, Typography } from '@material-ui/core';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import Cursor from '@utils/cursor';
import DOMPurify from 'dompurify';
import { PERSONALIZATION_TOKEN_TAG } from '@utils/personalization';

export function SLPInput({
  id,
  label,
  initialValue,
  isEditMode,
  onInput,
  onKeyDown,
  onClick,
  onDoubleClick,
  onCursorPositionChange,
  setValue,
}: SLPInputProps): JSX.Element {
  const styles = useStyles();

  const divElement = useRef<HTMLDivElement>(null);
  const lastHtml = useRef<string>('');

  const blockNextKeyInput = useRef<boolean>(false);

  // set the focus and cursor position when changing to edit mode
  // useEffect(() => {
  //   if (isEditMode) {
  //     if (divElement.current) {
  //       const html = divElement.current.innerHTML.replaceAll('&amp;', `&`).replaceAll('&nbsp;', ` `);

  //       const startPos = html.length > 0 ? html.length : 0;
  //       Cursor.setCursorPosition(divElement.current, startPos);
  //     }
  //   }
  // }, [isEditMode]);

  const handleOnFocus = useCallback((event: React.FocusEvent<HTMLDivElement>) => {}, []);

  const handleOnBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      if (isEditMode) {
        const cursorCurrentPosition = Cursor.getCursorPosition(event.currentTarget);

        if (divElement.current) {
          onCursorPositionChange(divElement.current, cursorCurrentPosition);
        }
      }
    },
    [isEditMode, onCursorPositionChange],
  );

  const handleOnClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (isEditMode) {
        const cursorCurrentPosition = Cursor.getCursorPosition(event.currentTarget);

        if (divElement.current) {
          onCursorPositionChange(divElement.current, cursorCurrentPosition);
        }

        onClick(event);
      }
    },
    [isEditMode, onCursorPositionChange, onClick],
  );

  const handleOnDoubleClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditMode) {
        event.preventDefault();
        onDoubleClick(event);

        if (divElement.current) {
          const html = divElement.current.innerHTML.replaceAll('&amp;', `&`).replaceAll('&nbsp;', ` `);

          const startPos = html.length > 0 ? html.length : 0;
          Cursor.setCursorPosition(divElement.current, startPos);
        }
      } else {
        const cursorCurrentPosition = Cursor.getCursorPosition(event.currentTarget);

        if (divElement.current) {
          onCursorPositionChange(divElement.current, cursorCurrentPosition);
        }

        onClick(event);
      }
    },
    [isEditMode, onDoubleClick, onClick, onCursorPositionChange],
  );

  const runOnInput = useMemo(
    () =>
      debounce((currentTarget: HTMLDivElement) => {
        if (!blockNextKeyInput.current) {
          const position = Cursor.getCursorPosition(currentTarget);

          const sanitizedHtml = DOMPurify.sanitize(currentTarget.innerHTML, {
            ALLOWED_TAGS: [PERSONALIZATION_TOKEN_TAG],
            ALLOWED_ATTR: ['style', 'data-attribute-name', 'contenteditable', 'initialvalue'],
          });

          const curHtml = sanitizedHtml;

          if (curHtml !== lastHtml.current) {
            setValue(sanitizedHtml);
            onInput(currentTarget);

            setTimeout(() => {
              if (divElement.current) {
                Cursor.setCursorPosition(divElement.current, position);
              }
            }, 0);

            lastHtml.current = curHtml ?? '';
          }
        }
      }, 10),
    [onInput, setValue],
  );

  const handleOnInput = useCallback(
    (event: React.FormEvent<HTMLDivElement>) => {
      runOnInput(event.currentTarget);
    },
    [runOnInput],
  );

  const handleOnBeforeInput = useCallback((event: React.FormEvent<HTMLDivElement>) => {
    if (blockNextKeyInput.current) {
      event.preventDefault();
    }
  }, []);

  const handleOnKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      blockNextKeyInput.current = false;
      // Allow Ctrl/Cmd + A, C, X keypresses
      if ((event.ctrlKey || event.metaKey) && (event.key === 'a' || event.key === 'c' || event.key === 'x')) {
        return;
      }

      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        return;
      }

      // prevent carriage returns
      if (event.key === 'Enter') {
        blockNextKeyInput.current = true;
        event.preventDefault();
        return;
      }

      if (divElement.current) {
        const charCount = getInnerHTMLLengthExcludingTokens(divElement.current);

        if (charCount >= 100) {
          if (event.key !== 'Backspace' && event.key !== 'Delete') {
            blockNextKeyInput.current = true;
            event.preventDefault();
          }
        }
      }

      onKeyDown(event);
    },
    [onKeyDown],
  );

  const handleOnPaste = useCallback((event: React.ClipboardEvent<HTMLDivElement>) => {
    // Prevent the default paste action
    event.preventDefault();

    // Get the pasted data
    const pasteData = event.clipboardData.getData('text');

    // Sanitize the pasted data
    const sanitizedData = DOMPurify.sanitize(pasteData, {
      ALLOWED_TAGS: [PERSONALIZATION_TOKEN_TAG],
      ALLOWED_ATTR: ['style', 'data-attribute-name', 'contenteditable', 'initialvalue'],
    });

    if (divElement.current) {
      const charCount = getInnerHTMLLengthExcludingTokens(divElement.current);

      if (charCount + sanitizedData.length > 100) {
        return;
      }
    }

    // TODO: Replace execCommand with non-deprecated alternative
    // Insert the sanitized data at the current cursor position
    document.execCommand('insertHTML', false, sanitizedData);
  }, []);

  return (
    <>
      <Typography variant="body2" className={[styles.textContainer, styles.fieldLabel].join(' ')}>
        <b>{label}</b>
      </Typography>
      <div className={styles.inputContainer}>
        <div
          id={id}
          className={`${styles.input} ${isEditMode ? styles.textEdit : styles.textNoEdit}`}
          contentEditable={isEditMode}
          onBlur={handleOnBlur}
          onClick={handleOnClick}
          onDoubleClick={handleOnDoubleClick}
          onKeyDown={handleOnKeyDown}
          onBeforeInput={handleOnBeforeInput}
          onInput={handleOnInput}
          onPaste={handleOnPaste}
          onFocus={handleOnFocus}
          dangerouslySetInnerHTML={{ __html: initialValue }}
          ref={divElement}
          data-name={id}
        ></div>
      </div>
    </>
  );
}
